import { useEffect, useRef } from 'react';
/**
 * Fires `onImpression` once when an element that started below the viewport
 * scrolls into view. Skipped if the element was already fully visible at mount.
 *
 * @param threshold Visible fraction that counts as "in view". Either the element
 *   itself reaches this ratio, or it fills this fraction of the viewport (the
 *   latter handles elements taller than the viewport). Default 0.75.
 * @param dwellMs Continuous in-view time before the impression fires. Default 1000.
 */
export const useBelowFoldImpression = function (ref, enabled, onImpression, _temp) {
  let {
    threshold = 0.75,
    dwellMs = 1000
  } = _temp === void 0 ? {} : _temp;
  const firedRef = useRef(false);
  const onImpressionRef = useRef(onImpression);
  onImpressionRef.current = onImpression;
  useEffect(() => {
    if (!enabled || firedRef.current) {
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      return;
    }
    if (el.getBoundingClientRect().bottom <= window.innerHeight) {
      firedRef.current = true;
      return;
    }
    let dwellTimer = null;
    const isInView = entry => {
      var _entry$rootBounds;
      if (entry.intersectionRatio >= threshold) {
        return true;
      }
      const viewportHeight = ((_entry$rootBounds = entry.rootBounds) == null ? void 0 : _entry$rootBounds.height) ?? 0;
      if (viewportHeight <= 0) {
        return false;
      }
      return entry.intersectionRect.height / viewportHeight >= threshold;
    };
    const observer = new IntersectionObserver(_ref => {
      let [entry] = _ref;
      if (!entry) {
        return;
      }
      if (isInView(entry)) {
        if (dwellTimer) {
          return;
        }
        dwellTimer = setTimeout(() => {
          if (firedRef.current) {
            return;
          }
          firedRef.current = true;
          onImpressionRef.current();
          observer.disconnect();
        }, dwellMs);
      } else if (dwellTimer) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    }, {
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });
    observer.observe(el);
    return () => {
      if (dwellTimer) {
        clearTimeout(dwellTimer);
      }
      observer.disconnect();
    };
  }, [enabled, ref, threshold, dwellMs]);
};
//# sourceMappingURL=useBelowFoldImpression.js.map